import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../auth-lib';
import AdminLayout from '../components/AdminLayout';
import { Button, Card, Input, Select, Skeleton, useToast } from '../../design-system';
import '../styles/AdminLanding.css';

// The super admin's editor for the PUBLIC marketing page (`/`).
//
// ⚠️ THIS IS THE ONLY SCREEN IN THE PLATFORM THAT PUBLISHES TO STRANGERS.
// Everything typed here is served unauthenticated at GET /api/landing, so the
// warning at the top of the page is not decoration — somebody editing this has
// to know that "save" means "publish to the internet", which is a different
// thing from every other save in this product.
//
// ⚠️ It ships EMPTY, deliberately. No sample testimonials, no placeholder
// customer counts, no "5,000+ firms" waiting to be swapped out. A trust signal
// is a claim the platform owner is personally making; pre-filling a plausible
// fake one is how an invented endorsement quietly goes live because nobody
// remembered it was a sample. Empty lists simply hide their section.

const ICON_CHOICES = [
  'spark', 'calendar', 'shield', 'invoice', 'chart', 'people', 'lock', 'bolt',
];

// Human labels for the layout slots the page supports. The keys are fixed in
// landingController.SECTION_KEYS — the server drops anything else, because a
// section it cannot place would save fine and then render nowhere.
const SECTION_LABELS = {
  // ⚠️ These two do NOT render as a card grid — `problem` is a struck list and
  // `steps` a numbered sequence. Same fields, different shape; the icon you pick
  // is ignored on both, since neither draws one.
  problem: 'The problem (struck list)',
  steps: 'How it works (numbered)',
  compliance: 'Compliance calendar',
  clients: 'The client record',
  automation: 'Portal automation',
  billing: 'Billing & finance',
  portal: 'Client portal',
  people: 'Staff, attendance & leave',
  renewals: 'Renewals & expiries',
  security: 'Security & audit',
  reports: 'Reports',
};

const emptyFeature = () => ({ icon: 'spark', title: '', body: '' });
const emptyStat = () => ({ value: '', label: '' });
const emptyTestimonial = () => ({ quote: '', name: '', role: '', firm: '' });
const emptyPlan = () => ({ name: '', price: '', period: 'per user / month', blurb: '', features: [], ctaLabel: 'Start free', ctaHref: '/register', isFeatured: false });
const emptyFaq = () => ({ question: '', answer: '' });

// A repeater block. Every list on this page is the same shape — add, remove,
// edit a row — so it is one component rather than six near-identical ones.
function Repeater({ label, hint, rows, onChange, makeEmpty, addLabel, children }) {
  return (
    <div className="al-repeater">
      <div className="al-repeater-head">
        <div>
          <span className="ds-field-label">{label}</span>
          {hint && <p className="al-hint">{hint}</p>}
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...rows, makeEmpty()])}>
          {addLabel || '+ Add'}
        </Button>
      </div>
      {rows.length === 0 && (
        <p className="al-empty">
          Nothing here yet — this section is hidden on the public page until you add something.
        </p>
      )}
      {rows.map((row, i) => (
        <div className="al-row" key={row._id || i}>
          <div className="al-row-head">
            <span className="ds-field-label">{label.replace(/s$/, '')} {i + 1}</span>
            <button
              type="button"
              className="btn-remove-icon"
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
              aria-label="Remove"
            >
              ×
            </button>
          </div>
          {children(row, (patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r))))}
        </div>
      ))}
    </div>
  );
}

function AdminLanding() {
  const toast = useToast();
  const [c, setC] = useState(null);
  const [sectionKeys, setSectionKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    apiClient
      .get('/api/landing/admin')
      .then((r) => { setC(r.data.content); setSectionKeys(r.data.sectionKeys || []); })
      .catch(() => toast.danger('Could not load the landing page content'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [load]);

  const set = (patch) => setC((prev) => ({ ...prev, ...patch }));
  const setIn = (key, patch) => setC((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const save = async (publishOverride) => {
    setSaving(true);
    const payload = { ...c };
    if (publishOverride !== undefined) payload.isPublished = publishOverride;
    try {
      const r = await apiClient.put('/api/landing/admin', payload);
      setC(r.data.content);
      toast.success(payload.isPublished ? 'Saved and live' : 'Saved as a draft');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !c) {
    return <AdminLayout title="Landing page"><Card><Skeleton.Text lines={6} /></Card></AdminLayout>;
  }

  const usedKeys = new Set((c.sections || []).map((s) => s.key));
  const availableKeys = sectionKeys.filter((k) => !usedKeys.has(k));

  return (
    <AdminLayout title="Landing page" subtitle="The public page at / — what people see before they sign in">
      {/* Said plainly and first. Every other save in this product changes what
          a firm sees; this one changes what the internet sees. */}
      <div className={`al-banner ${c.isPublished ? 'is-live' : ''}`} role="status">
        <strong>{c.isPublished ? '● Live' : '○ Draft — not visible to anyone'}</strong>
        <span>
          {c.isPublished
            ? 'This page is public at / and served without a login. Anything you type here is published.'
            : 'While this is a draft, / behaves as it did before: visitors go straight to sign in.'}
        </span>
        <div className="al-banner-actions">
          <Button variant="secondary" onClick={() => save(false)} disabled={saving || !c.isPublished}>
            Unpublish
          </Button>
          <Button variant="primary" onClick={() => save(true)} loading={saving}>
            {c.isPublished ? 'Save changes' : 'Publish'}
          </Button>
        </div>
      </div>

      <Card>
        <Card.Header title="Brand" />
        <div className="form-row">
          <Input label="Product name" value={c.brandName || ''} onChange={(e) => set({ brandName: e.target.value })} />
          <Input label="Tagline" value={c.tagline || ''} onChange={(e) => set({ tagline: e.target.value })} />
        </div>
        <div className="form-row">
          <Input label="Contact email" value={c.contactEmail || ''} onChange={(e) => set({ contactEmail: e.target.value })} />
          <Input label="Footer note" value={c.footerNote || ''} onChange={(e) => set({ footerNote: e.target.value })} />
        </div>
      </Card>

      <Card>
        <Card.Header title="Hero" />
        <div className="form-row">
          <Input label="Eyebrow" value={c.hero?.eyebrow || ''} onChange={(e) => setIn('hero', { eyebrow: e.target.value })} placeholder="Small line above the headline" />
        </div>
        <Input label="Headline" value={c.hero?.title || ''} onChange={(e) => setIn('hero', { title: e.target.value })} />
        <label className="ds-field">
          <span className="ds-field-label">Sub-headline</span>
          <textarea
            className="al-textarea"
            rows={3}
            value={c.hero?.subtitle || ''}
            onChange={(e) => setIn('hero', { subtitle: e.target.value })}
          />
        </label>
        <div className="form-row">
          <Input label="Primary button" value={c.hero?.primaryCta?.label || ''} onChange={(e) => setIn('hero', { primaryCta: { ...c.hero?.primaryCta, label: e.target.value } })} />
          <Input label="Primary link" value={c.hero?.primaryCta?.href || ''} onChange={(e) => setIn('hero', { primaryCta: { ...c.hero?.primaryCta, href: e.target.value } })} placeholder="/register" />
        </div>
        <div className="form-row">
          <Input label="Secondary button" value={c.hero?.secondaryCta?.label || ''} onChange={(e) => setIn('hero', { secondaryCta: { ...c.hero?.secondaryCta, label: e.target.value } })} />
          <Input label="Secondary link" value={c.hero?.secondaryCta?.href || ''} onChange={(e) => setIn('hero', { secondaryCta: { ...c.hero?.secondaryCta, href: e.target.value } })} />
        </div>
        <Input
          label="Reassurances under the buttons"
          value={(c.hero?.notes || []).join(', ')}
          onChange={(e) => setIn('hero', { notes: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          helperText="Comma separated — e.g. No card required, Cancel anytime"
        />
      </Card>

      <Card>
        <Card.Header title="Headline numbers" />
        {/* The nudge that matters. These are the numbers a stranger will read
            as fact about the business. */}
        <p className="al-warn">
          ⚠️ These are published claims about your business. Only enter figures you can stand behind —
          nothing here is calculated from the platform, and nothing is pre-filled.
        </p>
        <Repeater label="Stats" rows={c.stats || []} onChange={(stats) => set({ stats })} makeEmpty={emptyStat} addLabel="+ Add stat">
          {(row, patch) => (
            <div className="form-row">
              <Input label="Value" value={row.value} onChange={(e) => patch({ value: e.target.value })} placeholder="e.g. 5,000+" />
              <Input label="Label" value={row.label} onChange={(e) => patch({ label: e.target.value })} placeholder="e.g. CAs using it" />
            </div>
          )}
        </Repeater>
      </Card>

      <Card>
        <Card.Header
          title="Feature sections"
          action={
            availableKeys.length > 0 && (
              <Select
                value=""
                aria-label="Add a section"
                onChange={(e) => {
                  if (!e.target.value) return;
                  set({ sections: [...(c.sections || []), { key: e.target.value, eyebrow: '', title: '', body: '', isVisible: true, features: [] }] });
                  e.target.value = '';
                }}
              >
                <option value="">+ Add a section…</option>
                {availableKeys.map((k) => <option key={k} value={k}>{SECTION_LABELS[k] || k}</option>)}
              </Select>
            )
          }
        />
        {(c.sections || []).length === 0 && <p className="al-empty">No sections yet.</p>}
        {(c.sections || []).map((s, i) => {
          const patchSection = (p) => set({ sections: c.sections.map((x, j) => (j === i ? { ...x, ...p } : x)) });
          return (
            <div className="al-section-block" key={s._id || s.key}>
              <div className="al-row-head">
                <span className="ds-field-label">{SECTION_LABELS[s.key] || s.key}</span>
                <div className="al-row-actions">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={s.isVisible !== false} onChange={(e) => patchSection({ isVisible: e.target.checked })} />
                    Visible
                  </label>
                  <button type="button" className="btn-remove-icon" onClick={() => set({ sections: c.sections.filter((_, j) => j !== i) })} aria-label="Remove section">×</button>
                </div>
              </div>
              <div className="form-row">
                <Input label="Eyebrow" value={s.eyebrow || ''} onChange={(e) => patchSection({ eyebrow: e.target.value })} />
                <Input label="Title" value={s.title || ''} onChange={(e) => patchSection({ title: e.target.value })} />
              </div>
              <Input label="Body" value={s.body || ''} onChange={(e) => patchSection({ body: e.target.value })} />
              <Repeater
                label="Features"
                rows={s.features || []}
                onChange={(features) => patchSection({ features })}
                makeEmpty={emptyFeature}
                addLabel="+ Add feature"
              >
                {(f, patch) => (
                  <>
                    <div className="form-row">
                      <Select label="Icon" value={f.icon} onChange={(e) => patch({ icon: e.target.value })}>
                        {ICON_CHOICES.map((n) => <option key={n} value={n}>{n}</option>)}
                      </Select>
                      <Input label="Title" value={f.title} onChange={(e) => patch({ title: e.target.value })} />
                    </div>
                    <Input label="Body" value={f.body} onChange={(e) => patch({ body: e.target.value })} />
                  </>
                )}
              </Repeater>
            </div>
          );
        })}
      </Card>

      <Card>
        <Card.Header title="Testimonials" />
        <p className="al-warn">
          ⚠️ Only quote people who have actually agreed to appear. Nothing here is sample text —
          whatever you type is published under that person&apos;s name.
        </p>
        <Repeater label="Testimonials" rows={c.testimonials || []} onChange={(testimonials) => set({ testimonials })} makeEmpty={emptyTestimonial} addLabel="+ Add testimonial">
          {(row, patch) => (
            <>
              <label className="ds-field">
                <span className="ds-field-label">Quote</span>
                <textarea className="al-textarea" rows={3} value={row.quote} onChange={(e) => patch({ quote: e.target.value })} />
              </label>
              <div className="form-row">
                <Input label="Name" value={row.name} onChange={(e) => patch({ name: e.target.value })} />
                <Input label="Role" value={row.role} onChange={(e) => patch({ role: e.target.value })} />
                <Input label="Firm" value={row.firm} onChange={(e) => patch({ firm: e.target.value })} />
              </div>
            </>
          )}
        </Repeater>
      </Card>

      <Card>
        <Card.Header title="Pricing" />
        <div className="form-row">
          <Input label="Eyebrow" value={c.pricing?.eyebrow || ''} onChange={(e) => setIn('pricing', { eyebrow: e.target.value })} />
          <Input label="Title" value={c.pricing?.title || ''} onChange={(e) => setIn('pricing', { title: e.target.value })} />
        </div>
        <Input label="Body" value={c.pricing?.body || ''} onChange={(e) => setIn('pricing', { body: e.target.value })} />
        <Repeater
          label="Plans"
          rows={c.pricing?.plans || []}
          onChange={(plans) => setIn('pricing', { plans })}
          makeEmpty={emptyPlan}
          addLabel="+ Add plan"
        >
          {(p, patch) => (
            <>
              <div className="form-row">
                <Input label="Name" value={p.name} onChange={(e) => patch({ name: e.target.value })} />
                <Input label="Price" value={p.price} onChange={(e) => patch({ price: e.target.value })} placeholder="₹125" />
                <Input label="Period" value={p.period} onChange={(e) => patch({ period: e.target.value })} />
              </div>
              <Input label="Blurb" value={p.blurb} onChange={(e) => patch({ blurb: e.target.value })} />
              <Input
                label="What's included"
                value={(p.features || []).join('\n')}
                onChange={(e) => patch({ features: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
                helperText="One per line"
              />
              <div className="form-row">
                <Input label="Button label" value={p.ctaLabel} onChange={(e) => patch({ ctaLabel: e.target.value })} />
                <Input label="Button link" value={p.ctaHref} onChange={(e) => patch({ ctaHref: e.target.value })} />
                <label className="checkbox-label">
                  <input type="checkbox" checked={!!p.isFeatured} onChange={(e) => patch({ isFeatured: e.target.checked })} />
                  Highlight this plan
                </label>
              </div>
            </>
          )}
        </Repeater>
      </Card>

      <Card>
        <Card.Header title="FAQ" />
        <Repeater label="Questions" rows={c.faq || []} onChange={(faq) => set({ faq })} makeEmpty={emptyFaq} addLabel="+ Add question">
          {(row, patch) => (
            <>
              <Input label="Question" value={row.question} onChange={(e) => patch({ question: e.target.value })} />
              <label className="ds-field">
                <span className="ds-field-label">Answer</span>
                <textarea className="al-textarea" rows={3} value={row.answer} onChange={(e) => patch({ answer: e.target.value })} />
              </label>
            </>
          )}
        </Repeater>
      </Card>

      <Card>
        <Card.Header title="Closing call to action" />
        <div className="form-row">
          <Input label="Title" value={c.closing?.title || ''} onChange={(e) => setIn('closing', { title: e.target.value })} />
          <Input label="Body" value={c.closing?.body || ''} onChange={(e) => setIn('closing', { body: e.target.value })} />
        </div>
        <div className="form-row">
          <Input label="Button label" value={c.closing?.cta?.label || ''} onChange={(e) => setIn('closing', { cta: { ...c.closing?.cta, label: e.target.value } })} />
          <Input label="Button link" value={c.closing?.cta?.href || ''} onChange={(e) => setIn('closing', { cta: { ...c.closing?.cta, href: e.target.value } })} />
        </div>
      </Card>

      <Card>
        <Card.Header title="Search engines" />
        <div className="form-row">
          <Input label="Page title" value={c.seo?.metaTitle || ''} onChange={(e) => setIn('seo', { metaTitle: e.target.value })} />
          <Input label="Meta description" value={c.seo?.metaDescription || ''} onChange={(e) => setIn('seo', { metaDescription: e.target.value })} />
        </div>
      </Card>

      <div className="al-footer-actions">
        <Button variant="secondary" onClick={load} disabled={saving}>Discard changes</Button>
        <Button variant="primary" onClick={() => save()} loading={saving}>
          Save
        </Button>
        {c.updatedByName && (
          <span className="al-meta">Last saved by {c.updatedByName}</span>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminLanding;
